import { SavedAffirmation } from "../types";

const DB_NAME = "ManifestationDB";
const DB_VERSION = 1;
const STORE_NAME = "affirmations";
const FALLBACK_STORAGE_KEY = "manifestation_affirmations";

/**
 * Initialize the IndexedDB database
 */
const initDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onerror = (event) => {
      console.error("IndexedDB error:", event);
      reject("Failed to open database");
    };
    
    request.onsuccess = () => {
      resolve(request.result);
    };
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      // Create an object store for affirmations if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
    };
  });
};

/**
 * Save affirmations to IndexedDB
 */
export const saveAffirmations = async (affirmations: SavedAffirmation[]): Promise<void> => {
  // Filter out any affirmations with empty text
  const validAffirmations = affirmations.filter(aff => aff.text && aff.text.trim() !== "");
  
  // If no valid affirmations, clear storage instead of silently returning
  if (!validAffirmations || !validAffirmations.length) {
    await clearAllAffirmations();
    return;
  }
  
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    
    // Clear existing records
    store.clear();
    
    // Add all affirmations
    for (const affirmation of validAffirmations) {
      try {
        const affirmationToSave = {
          ...affirmation,
          // Ensure createdAt is serializable
          createdAt: affirmation.createdAt instanceof Date 
            ? affirmation.createdAt.toISOString() 
            : typeof affirmation.createdAt === 'string'
              ? affirmation.createdAt
              : new Date().toISOString()
        };
        
        store.add(affirmationToSave);
      } catch (err) {
        console.error("Error adding affirmation:", affirmation, err);
      }
    }
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log("Successfully saved affirmations to IndexedDB");
        
        // Also save to localStorage as a backup
        try {
          localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(validAffirmations));
          console.log("Backup saved to localStorage");
        } catch (storageError) {
          console.warn("Failed to save backup to localStorage", storageError);
        }
        
        db.close();
        resolve();
      };
      
      transaction.onerror = (event) => {
        console.error("Transaction error:", event);
        db.close();
        reject("Failed to save affirmations");
      };
    });
  } catch (error) {
    console.error("Error saving affirmations to IndexedDB:", error);
    
    // Fallback to localStorage if IndexedDB fails
    try {
      localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(validAffirmations));
      console.log("Saved to localStorage as fallback");
    } catch (storageError) {
      console.error("Error saving to localStorage as fallback:", storageError);
    }
  }
};

/**
 * Load affirmations from IndexedDB
 */
export const loadAffirmations = async (): Promise<SavedAffirmation[]> => {
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readonly");
    const store = transaction.objectStore(STORE_NAME);
    const request = store.getAll();
    
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const affirmations = request.result as Array<SavedAffirmation & { createdAt: string }>;
        
        // Convert dates and filter out empty affirmations
        const processedAffirmations = affirmations
          .map(aff => ({ ...aff, createdAt: new Date(aff.createdAt) }))
          .filter(aff => aff.text && aff.text.trim() !== "");
        
        console.log("Loaded from IndexedDB:", processedAffirmations);
        db.close();
        
        if (processedAffirmations.length === 0) {
          // Try fallback storage if no affirmations found in IndexedDB
          const fallbackAffirmations = loadFromLocalStorage();
          if (fallbackAffirmations.length > 0) {
            console.log("Using fallback affirmations from localStorage:", fallbackAffirmations);
            
            // Save fallback data to IndexedDB for next time
            setTimeout(() => {
              saveAffirmations(fallbackAffirmations).catch(console.error);
            }, 500);
            
            resolve(fallbackAffirmations);
          } else {
            resolve([]);
          }
        } else {
          resolve(processedAffirmations);
        }
      };
      
      request.onerror = (event) => {
        console.error("Request error:", event);
        db.close();
        
        // Try fallback
        const fallbackAffirmations = loadFromLocalStorage();
        console.log("Using fallback affirmations after IndexedDB error:", fallbackAffirmations);
        resolve(fallbackAffirmations);
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error("Error loading affirmations from IndexedDB:", error);
    
    // Fallback to localStorage
    const fallbackAffirmations = loadFromLocalStorage();
    console.log("Using fallback affirmations after error:", fallbackAffirmations);
    return fallbackAffirmations;
  }
};

/**
 * Helper function to load from localStorage
 */
const loadFromLocalStorage = (): SavedAffirmation[] => {
  try {
    const saved = localStorage.getItem(FALLBACK_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved) as SavedAffirmation[];
      // Ensure dates are converted back to Date objects
      return parsed.map(aff => ({
        ...aff,
        createdAt: new Date(aff.createdAt)
      }));
    }
  } catch (storageError) {
    console.error("Error loading from localStorage as fallback:", storageError);
  }
  
  return [];
};

/**
 * Delete a specific affirmation by ID
 */
export const deleteAffirmation = async (id: string): Promise<void> => {
  console.log("Deleting affirmation with ID:", id);
  
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    
    store.delete(id);
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log("Successfully deleted affirmation");
        db.close();
        
        // Update localStorage backup too
        try {
          const saved = localStorage.getItem(FALLBACK_STORAGE_KEY);
          if (saved) {
            const parsed = JSON.parse(saved) as SavedAffirmation[];
            const updated = parsed.filter(aff => aff.id !== id);
            localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(updated));
          }
        } catch (err) {
          console.warn("Error updating localStorage after deletion:", err);
        }
        
        resolve();
      };
      
      transaction.onerror = (event) => {
        console.error("Delete transaction error:", event);
        db.close();
        reject("Failed to delete affirmation");
      };
    });
  } catch (error) {
    console.error("Error deleting affirmation from IndexedDB:", error);
    
    // Try to update localStorage anyway
    try {
      const saved = localStorage.getItem(FALLBACK_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved) as SavedAffirmation[];
        const updated = parsed.filter(aff => aff.id !== id);
        localStorage.setItem(FALLBACK_STORAGE_KEY, JSON.stringify(updated));
      }
    } catch (err) {
      console.warn("Error updating localStorage after deletion attempt:", err);
    }
  }
};

/**
 * Clear all saved affirmations
 */
export const clearAllAffirmations = async (): Promise<void> => {
  console.log("Clearing all saved affirmations");
  
  try {
    const db = await initDB();
    const transaction = db.transaction(STORE_NAME, "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    
    // Clear all records
    store.clear();
    
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => {
        console.log("Successfully cleared all affirmations from IndexedDB");
        
        // Also clear localStorage backup
        try {
          localStorage.removeItem(FALLBACK_STORAGE_KEY);
          console.log("Backup cleared from localStorage");
        } catch (storageError) {
          console.warn("Failed to clear backup from localStorage", storageError);
        }
        
        db.close();
        resolve();
      };
      
      transaction.onerror = (event) => {
        console.error("Transaction error:", event);
        db.close();
        reject("Failed to clear affirmations");
      };
    });
  } catch (error) {
    console.error("Error clearing affirmations from IndexedDB:", error);
    
    // Fallback to localStorage if IndexedDB fails
    try {
      localStorage.removeItem(FALLBACK_STORAGE_KEY);
      console.log("Cleared localStorage as fallback");
    } catch (storageError) {
      console.error("Error clearing localStorage as fallback:", storageError);
    }
  }
};
